// ===== Scope Switch (팀/개인 스코프 토글 칩) =====
// 업무 후보/제안 검토/업무 보드가 공유하는 스코프 선택 위젯.
// 불변식: 최소 1개 스코프는 항상 활성. multi는 토글, single은 단일선택으로 동작한다.

const ScopeSwitch = (() => {
  type CoreScope = "team" | "personal";
  interface CoreScopeSwitchOptions {
    viewId?: string;
    personalEnabled?: boolean;
    selectionMode?: "multi" | "single";
    labels?: Partial<Record<CoreScope, string>>;
    ariaLabel?: string;
    onChange?: (value: { scopes: CoreScope[] }) => void;
  }

  const STORAGE_PREFIX = "scope-switch:";
  const ALL_SCOPES: CoreScope[] = ["team", "personal"];
  const CHIP_LABELS: Record<CoreScope, string> = { team: "팀", personal: "개인" };
  // 필터의 의미를 hover로 설명한다: 팀=전체 가시, 개인=오너·협업자.
  const CHIP_TITLES: Record<CoreScope, string> = {
    team: "전체 업무 — 팀 전원의 모든 업무",
    personal: "내 업무 — 내가 오너이거나 협업자인 업무",
  };

  function _storageKey(viewId: string): string {
    return `${STORAGE_PREFIX}${viewId}`;
  }

  /** 저장된 스코프 로드 — 파싱 실패/둘 다 false는 기본값(둘 다 ON)으로 정규화 */
  function _load(viewId: string): CoreScope[] {
    try {
      const raw = localStorage.getItem(_storageKey(viewId));
      if (!raw) return [...ALL_SCOPES];
      const data: unknown = JSON.parse(raw);
      const rawScopes = typeof data === "object" && data !== null && "scopes" in data && Array.isArray(data.scopes)
        ? data.scopes
        : [];
      const scopes = [...new Set(rawScopes.filter((scope): scope is CoreScope =>
        scope === "team" || scope === "personal"))];
      return scopes.length > 0 ? scopes : [...ALL_SCOPES];
    } catch {
      return [...ALL_SCOPES];
    }
  }

  function _save(viewId: string, scopes: CoreScope[]): void {
    try {
      localStorage.setItem(_storageKey(viewId), JSON.stringify({ scopes }));
    } catch {
      // localStorage 불가 환경 — 지속 실패는 무시, 런타임 상태는 유지
    }
  }

  function _shake(chipEl: HTMLButtonElement): void {
    chipEl.classList.remove("scope-chip-shake");
    void chipEl.offsetWidth; // reflow로 애니메이션 재시작
    chipEl.classList.add("scope-chip-shake");
  }

  /**
   * ScopeSwitch.create({ viewId, personalEnabled, selectionMode, labels, onChange })
   * → 팀/개인 칩 2개를 가진 엘리먼트 반환
   */
  function create({ viewId, personalEnabled = true, selectionMode = "multi", labels = {}, ariaLabel = "업무 범위", onChange }: CoreScopeSwitchOptions = {}): HTMLDivElement {
    if (!viewId) throw new Error("ScopeSwitch.create: viewId is required");
    const storageViewId = viewId;

    let scopes = _load(storageViewId);
    if (selectionMode === "single" && scopes.length > 1) scopes = ["team"];
    if (!personalEnabled && scopes.includes("personal")) {
      scopes = scopes.filter((s) => s !== "personal");
      if (scopes.length === 0) scopes = ["team"];
    }

    const wrap = document.createElement("div");
    wrap.className = "scope-switch" + (selectionMode === "single" ? " scope-switch--single" : "");
    if (selectionMode === "single") {
      wrap.setAttribute("role", "radiogroup");
      wrap.setAttribute("aria-label", ariaLabel);
    }

    function _syncChip(chipEl: HTMLButtonElement, scope: CoreScope): void {
      const active = scopes.includes(scope);
      chipEl.classList.toggle("active", active);
      chipEl.setAttribute("aria-pressed", String(active));
      if (selectionMode === "single") chipEl.setAttribute("aria-checked", String(active));
    }

    function _handleClick(scope: CoreScope, chipEl: HTMLButtonElement): void {
      if (chipEl.disabled) return;
      const isActive = scopes.includes(scope);
      if (selectionMode === "single") {
        if (isActive) return;
        scopes = [scope];
        _syncChip(teamChip, "team");
        _syncChip(personalChip, "personal");
        _save(storageViewId, scopes);
        if (typeof onChange === "function") onChange({ scopes: [...scopes] });
        return;
      }
      if (isActive) {
        if (scopes.length <= 1) {
          // 마지막 활성 칩 — 무시 + 시각 피드백
          _shake(chipEl);
          return;
        }
        scopes = scopes.filter((s) => s !== scope);
      } else {
        scopes = [...scopes, scope];
      }
      _syncChip(teamChip, "team");
      _syncChip(personalChip, "personal");
      _save(storageViewId, scopes);
      if (typeof onChange === "function") onChange({ scopes: [...scopes] });
    }

    function _createChip(scope: CoreScope): HTMLButtonElement {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "scope-chip";
      chip.dataset.scope = scope;
      chip.textContent = labels[scope] || CHIP_LABELS[scope];
      chip.title = CHIP_TITLES[scope];
      if (selectionMode === "single") chip.setAttribute("role", "radio");
      chip.addEventListener("click", () => _handleClick(scope, chip));
      return chip;
    }

    const teamChip = _createChip("team");
    const personalChip = _createChip("personal");
    if (!personalEnabled) {
      personalChip.disabled = true;
      personalChip.classList.add("scope-chip-disabled");
    }
    _syncChip(teamChip, "team");
    _syncChip(personalChip, "personal");

    wrap.appendChild(teamChip);
    wrap.appendChild(personalChip);

    return wrap;
  }

  return { create };
})();
